import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getMedia, getProjectMedia } from '@/api/media';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import { env } from '@/constants/env';
import { SelectedImage } from '@/features/estate-media/components/selected-image';
import { createTempId } from '@/utils/tempId';
import { uniqBy } from '@/utils/uniqBy';
import { Modal } from '../../../components/modals/new/modal';
import type { useModal } from '../../../components/modals/new/use-modal';
import type { ImageInAtom } from '../atoms/imageFiles';
import { selectedImageAtom, uploadedImagesAtom } from '../atoms/imageFiles';

interface MediaImage {
  estateId: string | null;
  id: string;
  name: string | null;
  createdAt: string;
  creatorId: string | null;
  mediaId?: string | null;
  fileName: string | null;
}

type MediaLibraryBaseProps = {
  onSaveSelected?: (image: MediaImage | null) => void;
  modal: ReturnType<typeof useModal>;
};

export type MediaLibraryModalProps =
  | ({
      type: 'estate';
      estateId: string;
    } & MediaLibraryBaseProps)
  | ({
      type: 'project';
      projectId: string;
    } & MediaLibraryBaseProps);

export const MediaLibraryModal = (props: MediaLibraryModalProps) => {
  const { t } = useTranslation();
  const estateId = props.type === 'estate' ? props.estateId : undefined;
  const projectId = props.type === 'project' ? props.projectId : undefined;

  const [images, setImages] = useState<MediaImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingMediaImage, setPendingMediaImage] = useState<MediaImage | null>(null);
  const [pendingImage, setPendingImage] = useState<ImageInAtom | null>(null);
  const [, setSelectedImage] = useAtom(selectedImageAtom);
  const [, setUploadedImages] = useAtom(uploadedImagesAtom);

  const handleSelectImage = (image: MediaImage) => {
    if (pendingImage?.originalImageId === image.mediaId) {
      setPendingImage(null);
      setPendingMediaImage(null);
      return;
    }

    if (image.mediaId) {
      setPendingImage({
        id: createTempId(),
        originalImage: `${env.API_URL}/images/${image.mediaId}`,
        originalImageId: image.mediaId,
        file: null,
        fileType: 'image/jpeg',
      } satisfies ImageInAtom);
      setPendingMediaImage(image);
    }
  };

  const handleSave = () => {
    if (props.onSaveSelected) {
      props.onSaveSelected(pendingMediaImage);
    }

    if (pendingImage) {
      setSelectedImage(pendingImage);
      setUploadedImages((prev) => [...prev, pendingImage]);
    }
    props.modal.dismiss();
  };

  const handleFetchImages = useCallback(async () => {
    try {
      setLoading(true);
      if (estateId) {
        // Estate media can be returned either as a flat array or grouped by date.
        const res = await getMedia(estateId);
        const rawImages = res.images ?? [];

        let imgs: MediaImage[];

        if (Array.isArray(rawImages)) {
          imgs = rawImages as MediaImage[];
        } else {
          imgs = Object.values(rawImages as Record<string, MediaImage[]>).flat();
        }

        setImages(imgs);
      } else if (projectId) {
        const res = await getProjectMedia(projectId);
        setImages(uniqBy(res.images ?? [], 'id'));
      }
    } finally {
      setLoading(false);
    }
  }, [estateId, projectId]);

  useEffect(() => {
    handleFetchImages();
  }, [handleFetchImages]);

  return (
    <Modal
      {...props.modal}
      title={t('mediaLibrary')}
      className="flex max-h-[90vh] w-full max-w-[900px] flex-col overflow-hidden rounded-sm p-0"
    >
      <Modal.Body className="flex max-h-[calc(90vh-72px)] flex-1 flex-col overflow-auto p-4 pb-[75px]">
        {loading ? (
          <div className="flex flex-1 items-center justify-center bg-background">
            <Spinner className="size-12 [&_*]:stroke-primary-dark" />
          </div>
        ) : images.length ? (
          <>
            <div className="grid min-w-[720px] grid-cols-6 gap-1">
              {images.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className="cursor-pointer whitespace-normal break-words px-1"
                  onClick={() => handleSelectImage(file)}
                >
                  <SelectedImage
                    fileType={'fileType' in file ? (file.fileType as string) : ''}
                    fileName={file.fileName as string}
                    mediaId={file?.mediaId as string}
                    onClick={() => handleSelectImage(file)}
                    isSelected={pendingImage?.originalImageId === file.mediaId}
                  />
                </button>
              ))}
            </div>
            <div className="absolute bottom-0 left-0 mt-auto flex h-[70px] w-full items-center justify-end bg-white pr-4">
              <Button disabled={!pendingImage} onClick={handleSave}>
                {t('save')}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <span>{t('noContent')}</span>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
